import { Box, Modal, Table, Typography } from "components";
import Notification from "components/common/Notification";
import { format, getYear, parseISO } from "date-fns";
import { t } from "i18next";
import { cloneDeep, get, isString } from "lodash";
import { default as orgOmitDeep } from "omit-deep";
import { MASTER_MENU, TENANTED_MENU } from "routes/menu";
import paths, { Path, getTenantIdFromUrl } from "routes/paths";
import appStore from "stores/appStore";
import { AppColor } from "theme/app-color";
import { ValidationError } from "yup";
import env from "env";
export { gql } from "@apollo/client";
export { default as deepEqual } from "deep-equal";
export { default as paths } from "routes/paths";
export { sprintf } from "sprintf-js";
export * as Yup from "yup";

export const toDate = (date) => {
  if (isString(date)) {
    date = parseISO(date);
  }
  return date;
};

export const formatDate = (date, pattern) => {
  if (isString(date)) {
    date = parseISO(date);
  }

  if (date) {
    pattern = pattern.replace("be(yyyy)", `${getYear(date) + 543}`);
    return format(date, pattern);
  } else {
    return null;
  }
};

export function filterEmptyValues(obj) {
  const result = {};

  for (const key of Object.keys(obj)) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      if (value) {
        result[key] = value;
      }
    }
  }

  return result;
}

export const allOption = { label: "ทั้งหมด", value: "all" };
export const filterOutAllOption = (values) => {
  // console.log({ values });
  if (values) {
    // ตรวจสอบว่า options คือ object ที่สามารถใช้ Object.entries ได้
    if (typeof values !== "object" || values === null) {
      throw new TypeError("The provided argument is not an object.");
    }

    const filteredOptions = Object.fromEntries(
      Object.entries(values)
        .filter(([key, value]) => value !== allOption.value)
        .map(([key, value]) => {
          let convertedValue = value;
          if (typeof value === "string") {
            if (value.toLowerCase() === "true") {
              convertedValue = true;
            } else if (value.toLowerCase() === "false") {
              convertedValue = false;
            }
          }
          return [key, convertedValue];
        })
    );
    return filteredOptions;
  }
  return {};
};

export const deepTransformStringToDate = (object) => {
  if (object instanceof ArrayBuffer) {
    return object;
  }
  if (object instanceof Array) {
    return object.map((obj) => deepTransformStringToDate(obj));
  }
  if (object instanceof Object) {
    return Object.entries(object).reduce(
      (memo, [key, value]) => ({
        ...memo,
        [key]: deepTransformStringToDate(value),
      }),
      {}
    );
  }
  if (typeof object === "string") {
    if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/.test(object)) {
      return new Date(object);
    }
  }
  return object;
};

export const getFullAddress = (info) => {
  const isBangkok = info.province === "กรุงเทพมหานคร";
  const subDistrictPrefix = isBangkok ? "แขวง" : "ตำบล";
  const districtPrefix = isBangkok ? "เขต" : "อำเภอ";
  return `${info.address ?? ""} ${
    info.subDistrict ? `${subDistrictPrefix} ${info.subDistrict}` : ""
  } ${info.district ? `${districtPrefix} ${info.district}` : ""} ${
    info.province ?? ""
  } ${info.zipCode ?? ""}`
    .replace(/ +/g, " ")
    .replace(/^ *$/, "");
};

// prettier-ignore
const CARBON_FOOTPRINT_TABLE = {
  knit: {
    polyester_100: { white:  5.44, light:  8.96, medium: 11.21, dark: 15.22, very_dark: 19.33 },
    tc:            { white: 11.56, light: 15.21, medium: 17.25, dark: 20.89, very_dark: 24.61 },
    cvc:           { white: 11.69, light: 15.36, medium: 17.20, dark: 20.46, very_dark: 23.81 },
    cotton_100:    { white: 12.62, light: 16.39, medium: 18.05, dark: 21.00, very_dark: 24.02 },
  },
  woven: {
    polyester_100: { white:  9.41, light: 13.01, medium: 15.26, dark: 19.27, very_dark: 23.37 },
    tc:            { white: 15.91, light: 19.77, medium: 21.82, dark: 25.45, very_dark: 29.18 },
    cvc:           { white: 17.17, light: 21.25, medium: 23.09, dark: 26.35, very_dark: 29.70 },
    cotton_100:    { white: 18.24, light: 22.58, medium: 24.23, dark: 27.18, very_dark: 30.20 },
  },
};

// prettier-ignore
const WATER_FOOTPRINT_TABLE = {
  knit: {
    polyester_100: { white: null, light: null, medium: null, dark: null, very_dark: null },
    tc:            { white: null, light: null, medium: null, dark: null, very_dark: null },
    cvc:           { white: null, light: null, medium: null, dark: null, very_dark: null },
    cotton_100:    { white: null, light: null, medium: null, dark: null, very_dark: null },
  },
  woven: {
    polyester_100: { white: null, light: null, medium: null, dark: null, very_dark: null },
    tc:            { white: null, light: null, medium: null, dark: null, very_dark: null },
    cvc:           { white: null, light: null, medium: null, dark: null, very_dark: null },
    cotton_100:    { white: null, light: null, medium: null, dark: null, very_dark: null },
  },
};

export function getCarbonFootprintPerKg(category, tone, carbonFootprintType) {
  const type = {
    knit_natural: "knit",
    knit_synthetic: "knit",
    knit_mixed: "knit",
    woven_natural: "woven",
    woven_synthetic: "woven",
    woven_mixed: "woven",
    non_woven: "knit",
  }[category];
  return get(CARBON_FOOTPRINT_TABLE, `${type}.${carbonFootprintType}.${tone}`);
}

export function getWaterFootprintPerKg(category, tone, carbonFootprintType) {
  const type = {
    knit_natural: "knit",
    knit_synthetic: "knit",
    knit_mixed: "knit",
    woven_natural: "woven",
    woven_synthetic: "woven",
    woven_mixed: "woven",
    non_woven: "knit",
  }[category];
  return get(WATER_FOOTPRINT_TABLE, `${type}.${carbonFootprintType}.${tone}`);
}

export function calculateKgToYard(gsm, fw, kg = 1) {
  const kgPerSyards = (gsm * fw) / 43.06 / 1000;
  const yardPerKg = 1 / kgPerSyards;
  return kg * yardPerKg;
}

export function toCurrency(number, options = {}) {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    ...options,
  }).format(number);
}

export function omitDeep(object, key) {
  return orgOmitDeep(cloneDeep(object), key);
}

export function cleanTypename(object) {
  return omitDeep(object, "__typename");
}

export function forceDownload(href, filename) {
  var anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename ?? href;
  document.body.appendChild(anchor);
  anchor.click();
}

export function notify(message) {
  Notification.notify(message);
}

export function notifySuccess(message) {
  Notification.success(message);
}

export function notifyError(error) {
  if (isString(error)) {
    Notification.error(error);
  } else {
    let errorMessage = null;
    try {
      errorMessage = error?.networkError?.result?.errors
        ?.map((e) => e.message)
        ?.join(", ");
    } catch (e) {}
    errorMessage =
      errorMessage ||
      error?.networkError?.result?.message ||
      "เกิดข้อผิดพลาดบางอย่าง โปรดติดต่อทีมงาน";
    Notification.error(errorMessage);
  }
}

export function getErrorMessage(error) {
  if (isString(error)) {
    return error;
  } else {
    let errorMessage = null;
    try {
      errorMessage = error?.networkError?.result?.errors
        ?.map((e) => e.message)
        ?.join(", ");
    } catch (e) {}
    errorMessage =
      errorMessage ||
      error?.networkError?.result?.message ||
      "เกิดข้อผิดพลาดบางอย่าง โปรดติดต่อทีมงาน";
    return errorMessage;
  }
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const showSplashScreen = (isShow = true) => {
  const $splashScreen = window.document.getElementById("splash-screen");
  const $appScreen = window.document.getElementById("root");
  if ($appScreen) {
    $appScreen.style.display = isShow ? "none" : "block";
  }
  if ($splashScreen) {
    $splashScreen.style.display = isShow ? "flex" : "none";
  }
};

export const redirect = (pathname) => {
  if (window.location.pathname !== pathname) {
    window.location.href = pathname;
  }
};

export const formatPhoneNumber = (str) => {
  let cleaned = ("" + str).replace(/\D/g, "");
  let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return "" + match[1] + "-" + match[2] + "-" + match[3];
  }
  return null;
};

export const formatFullName = (firstName, lastName) => {
  return `${firstName || ""} ${lastName || ""}`.replace(/^ $/, "");
};

export const formatBankNumber = (str) => {
  let cleaned = ("" + str).replace(/\D/g, "");
  let match = cleaned.match(/^(\d{3})(\d{1})(\d{5})(\d*)$/);
  if (match) {
    return "" + match[1] + "-" + match[2] + "-" + match[3] + "-" + match[4];
  }
  return null;
};

export const formatTaxId = (str) => {
  let cleaned = ("" + str).replace(/\D/g, "");
  let match = cleaned.match(/^(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})$/);
  if (match) {
    return (
      "" +
      match[1] +
      "-" +
      match[2] +
      "-" +
      match[3] +
      "-" +
      match[4] +
      "-" +
      match[5]
    );
  }
  return null;
};

const ranges = [
  { divider: 1e18, suffix: "E" },
  { divider: 1e15, suffix: "P" },
  { divider: 1e12, suffix: "T" },
  { divider: 1e9, suffix: "G" },
  { divider: 1e6, suffix: "M" },
  { divider: 1e3, suffix: "k" },
];

export const formatNumber = (n) => {
  let isNegativeNumber = false;
  if (n < 0) {
    isNegativeNumber = true;
    n = Math.abs(n);
  }
  for (var i = 0; i < ranges.length; i++) {
    if (n >= ranges[i].divider) {
      if (isNegativeNumber) {
        return (
          "-" + (n / ranges[i].divider).toFixed(2).toString() + ranges[i].suffix
        );
      } else {
        return (n / ranges[i].divider).toFixed(2).toString() + ranges[i].suffix;
      }
    }
  }

  return n.toString();
};

export const replaceNameSpace = (initPrefix, ns) => {
  return ns.replaceAll(/(^\.|\$t\(\.)(?=\w)/g, (match) => {
    if (match.length && match.length === 1) {
      return "client." + initPrefix + ".";
    } else {
      const withOutLastChar = match.substring(0, match.length - 1);
      return withOutLastChar + "client." + initPrefix + ".";
    }
  });
};

// List of known page path segments (used to strip tenant ID prefix from menu paths)
const STANDARD_PAGE_SEGMENTS = [
  "workspace",
  "admins",
  "roles",
  "settings",
  "insurance",
  "enrollment",
  "report",
  "marketplace_report",
  "insurance_company",
  "dynamic_config",
  "notification_management",
  "occupation",
  "master_insurance_plan",
  "payment_transaction",
  "insurance_transaction",
  "overdue_transaction",
  "lead",
  "user",
  "front_store_management",
  "broker",
  "name_change_request",
  "order",
];

/**
 * Strips the tenant ID prefix from a menu path and returns the page-only path.
 * Menu paths may contain a tenantId prefix (e.g. "/{tenantId}/order") because
 * they are computed at module load time from getTenantIdFromUrl().
 * If the first segment is NOT a known page name, it is treated as a tenantId and stripped.
 */
function getPagePath(rawPath) {
  let path = rawPath.toString();
  if (path.startsWith("/")) path = path.substring(1);
  const segments = path.split("/");
  const firstSegment = segments[0];
  if (firstSegment && !STANDARD_PAGE_SEGMENTS.includes(firstSegment)) {
    path = segments.slice(1).join("/");
  }
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Returns the first menu path (without tenant prefix) that the user has permission to access.
 * Iterates menus in declaration order (MASTER_MENU or TENANTED_MENU), including child menus.
 */
/**
 * Returns the first menu path (with tenant prefix if tenantId exists) that the user has permission to access.
 * Iterates menus in declaration order (MASTER_MENU or TENANTED_MENU), including child menus.
 */
export function homePath(permissions, isMaster = false) {
  const menuToUse = isMaster ? MASTER_MENU : TENANTED_MENU;
  const userPermissions =
    permissions || appStore.state.currentUser?.role?.permissions || [];

  const hasPermission = (requiredPermissions = []) =>
    requiredPermissions.every((perm) => userPermissions.includes(perm));

  let resultPath = "/admins";

  for (const menu of menuToUse) {
    if (menu.children && menu.children.length > 0) {
      // Group menu: find first child the user has permission for
      const permittedChild = menu.children.find((child) =>
        hasPermission(child.permissions)
      );
      if (permittedChild) {
        resultPath = getPagePath(permittedChild.path);
        break;
      }
    } else {
      // Leaf menu: check menu's own permissions
      if (hasPermission(menu.permissions)) {
        resultPath = getPagePath(menu.path);
        break;
      }
    }
  }

  const tenantId = getTenantIdFromUrl();
  if (tenantId) {
    return getTenantPath(resultPath, tenantId);
  }
  return resultPath;
}

export function getTenantPath(path, tenantId) {
  if (!path) return "";
  if (!tenantId) return path.toString();

  let cleanPath = path.toString();
  if (cleanPath.startsWith("/")) {
    cleanPath = cleanPath.substring(1);
  }
  const segments = cleanPath.split("/");
  const firstSegment = segments[0];
  if (firstSegment && !STANDARD_PAGE_SEGMENTS.includes(firstSegment)) {
    cleanPath = segments.slice(1).join("/");
  }
  return `/${tenantId}/${cleanPath}`.replace(/\/+/g, "/");
}

/**
 * Like homePath but returns a Path object (with push/reload methods) instead of a string.
 * Used when navigating within the app (without full page reload).
 */
export function homePathByPermissions(permissions, isMaster = false) {
  const menuToUse = isMaster ? MASTER_MENU : TENANTED_MENU;
  const userPermissions =
    permissions || appStore.state.currentUser?.role?.permissions || [];

  const hasPermission = (requiredPermissions = []) =>
    requiredPermissions.every((perm) => userPermissions.includes(perm));

  let resultPathObj = null;

  for (const menu of menuToUse) {
    if (menu.children && menu.children.length > 0) {
      // Group menu: find first child the user has permission for
      const permittedChild = menu.children.find((child) =>
        hasPermission(child.permissions)
      );
      if (permittedChild) {
        resultPathObj = new Path(permittedChild.path);
        break;
      }
    } else {
      // Leaf menu: check menu's own permissions
      if (hasPermission(menu.permissions)) {
        if (menu.pathFunction) {
          resultPathObj = menu.pathFunction();
        } else {
          resultPathObj = new Path(menu.path);
        }
        break;
      }
    }
  }

  if (!resultPathObj) {
    resultPathObj = paths.adminsPath();
  }

  const tenantId = getTenantIdFromUrl();
  if (tenantId) {
    return new Path(getTenantPath(resultPathObj.toString(), tenantId));
  }
  return resultPathObj;
}

/** Alias for homePath — kept for backwards compatibility. */
export const homeUrl = homePath;

export function publishedAlert(disablePreventLeaveDirtyForm) {
  Modal.alert({
    title: t("client.error.saveError"),
    children: t("client.error.saveErrorChildren"),
    okButtonLabel: t("client.error.accept"),
    onOk: async ({ close }) => {
      if (disablePreventLeaveDirtyForm) await disablePreventLeaveDirtyForm();
      close();
      window.location.reload();
    },
    disableBackdropClick: true,
  });
}
export const convertFileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const padNumber = (val) => {
  return String(val).padStart(2, "0");
};

export const formatDateForDb = (date) => {
  return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(
    date.getDate()
  )}`;
};

export const formatDateForReportName = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}${month}${year}${hours}${minutes}`;
};
export const convertFileToUrl = (file) => {
  return URL.createObjectURL(file) ?? "";
};

// preview ที่ไม่ได้ถูก PreviewGuard ครอบ (เช่น landing link) — เปิด iframe ตรงๆ ไม่ต้องขอ preview token
export const handleClickViewInsurance = (url) => {
  Modal.open({
    title: "",
    fullWidth: true,
    maxWidth: "xs",
    PaperProps: {
      style: {
        padding: 0,
      },
    },
    contentStyle: {
      padding: 0,
    },
    children: (
      <iframe
        src={url}
        width="100%"
        height="812px"
        title="Preview Insurance"
        style={{
          border: "none",
          display: "block",
          width: "100%",
          height: "812px",
        }}
      ></iframe>
    ),
    hideFooter: true,
  });
};

// preview ของ route ที่ถูก PreviewGuard ครอบ (insurance-detail / policy-detail / policy-detail-another-version)
// ต้องแนบ preview_token ที่ขอจาก API ไปกับ url ก่อน ไม่งั้นฝั่ง app จะขึ้น Access Denied
export const openInsurancePreviewModal = async (url, onClose) => {
  try {
    const authToken = localStorage.getItem("AUTHENTICATION_TOKEN");
    const response = await fetch(`${env["API"]}/preview-token`, {
      method: "POST",
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) throw new Error("Failed to get preview token");
    const data = await response.json();
    const separator = url.includes("?") ? "&" : "?";
    const finalUrl = `${url}${separator}preview_token=${data.previewToken}`;
    Modal.open({
      title: "",
      fullWidth: true,
      maxWidth: "xs",
      PaperProps: {
        style: {
          padding: 0,
        },
      },
      contentStyle: {
        padding: 0,
      },
      children: (
        <iframe
          src={finalUrl}
          width="100%"
          height="812px"
          title="Preview Insurance"
          style={{
            border: "none",
            display: "block",
            width: "100%",
            height: "812px",
          }}
        ></iframe>
      ),
      hideFooter: true,
      onClose,
    });
  } catch {
    Modal.alert({
      title: "Error",
      children: "ไม่สามารถเปิด Preview ได้",
    });
  }
};

export const crossLanguageValidation = (thField, enField) => {
  return function (values) {
    const hasTh = !!values?.[thField]?.trim();
    const hasEn = !!values?.[enField]?.trim();

    const thPath = this.path ? `${this.path}.${thField}` : thField;
    const enPath = this.path ? `${this.path}.${enField}` : enField;

    if (hasTh && !hasEn) {
      throw new ValidationError([
        new ValidationError(
          "client.yup.error.requiredEn",
          values?.[thField],
          thPath
        ),
        new ValidationError(
          "client.yup.error.requiredEn",
          values?.[enField],
          enPath
        ),
      ]);
    }
    if (hasEn && !hasTh) {
      throw new ValidationError([
        new ValidationError(
          "client.yup.error.requiredTh",
          values?.[thField],
          thPath
        ),
        new ValidationError(
          "client.yup.error.requiredTh",
          values?.[enField],
          enPath
        ),
      ]);
    }
    return true;
  };
};

export const isEmptyHtml = (html) => {
  if (!html) return true;
  // Strip HTML tags and check if there's actual text content
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  return stripped.length === 0;
};

export const swapNameRequiredTest = function (values) {
  const nameTh = values?.nameTh ?? "";
  const nameEn = values?.nameEn ?? "";

  const pathPrefix = this?.path ? `${this.path}.` : "";

  if (nameTh.trim() === "") {
    throw new ValidationError([
      new ValidationError(".requiredTh", values?.nameTh, `${pathPrefix}nameTh`),
      new ValidationError(".requiredTh", values?.nameEn, `${pathPrefix}nameEn`),
    ]);
  }
  if (nameEn.trim() === "") {
    throw new ValidationError([
      new ValidationError(".requiredEn", values?.nameTh, `${pathPrefix}nameTh`),
      new ValidationError(".requiredEn", values?.nameEn, `${pathPrefix}nameEn`),
    ]);
  }
  return true;
};

export const swapLabelRequiredTest = function (values) {
  const labelTh = values?.labelTh ?? "";
  const labelEn = values?.labelEn ?? "";

  const pathPrefix = this?.path ? `${this.path}.` : "";

  if (labelTh.trim() === "") {
    throw new ValidationError([
      new ValidationError(
        ".requiredTh",
        values?.labelTh,
        `${pathPrefix}labelTh`
      ),
      new ValidationError(
        ".requiredTh",
        values?.labelEn,
        `${pathPrefix}labelEn`
      ),
    ]);
  }
  if (labelEn.trim() === "") {
    throw new ValidationError([
      new ValidationError(
        ".requiredEn",
        values?.labelTh,
        `${pathPrefix}labelTh`
      ),
      new ValidationError(
        ".requiredEn",
        values?.labelEn,
        `${pathPrefix}labelEn`
      ),
    ]);
  }
  return true;
};

// export const crossLanguageValidation = (thField, enField) => {
//   return function (value) {
//     const rawTh = value?.[thField] ?? "";
//     const rawEn = value?.[enField] ?? "";
//     const hasTh = !isEmptyHtml(rawTh) && !!rawTh.trim();
//     const hasEn = !isEmptyHtml(rawEn) && !!rawEn.trim();

//     if (hasTh && !hasEn) {
//       const path = this.path ? `${this.path}.${thField}` : thField;
//       return this.createError({ path, message: ".requiredEn" });
//     }
//     if (hasEn && !hasTh) {
//       const path = this.path ? `${this.path}.${enField}` : enField;
//       return this.createError({ path, message: ".requiredTh" });
//     }
//     return true;
//   };
// };

const stripHtml = (html = "") => {
  if (!html) return "";

  const temp = document.createElement("div");
  temp.innerHTML = html;

  return temp.textContent || temp.innerText || "";
};

export const stripHtmlInline = (html = "") => {
  return stripHtml(html).replace(/\s+/g, " ").trim();
};

export const displayValidateMasterInsurancePlanDependenciesErrorModal = (
  errors
) => {
  const columns = [
    {
      field: "name",
      headerName: t(
        "client.validateMasterInsurancePlanDependenciesErrorModal.rider"
      ),
      width: 250,
    },
    {
      field: "dependentName",
      headerName: t(
        "client.validateMasterInsurancePlanDependenciesErrorModal.dependent"
      ),
      flex: 1,
    },
  ];
  const tableData = errors.map((item) => ({
    id: item.id,
    name: item.name,
    dependentName: item.missDependents
      .map((dependent) => dependent.name)
      .join(", "),
  }));
  Modal.alert({
    title: t(
      "client.validateMasterInsurancePlanDependenciesErrorModal.saveUnsuccess"
    ),
    children: (
      <Box display="flex" flexDirection="column">
        <Typography variant="Body">
          {t(
            "client.validateMasterInsurancePlanDependenciesErrorModal.invalidDependentContent"
          )}
        </Typography>
        <Box
          className="ConditionModalTableScroll"
          style={{ marginTop: "24px", marginBottom: "24px" }}
        >
          <Table
            columns={columns}
            rows={tableData}
            density="compact"
            hideFooter
            autoPageSize={false}
            hideFooterPagination
            hideFooterRowCount
            hideFooterSelectedRowCount
            style={{ minHeight: 0 }}
          />
        </Box>
      </Box>
    ),
    okButtonType: "button",
    okButtonLabel: t(
      "client.validateMasterInsurancePlanDependenciesErrorModal.accept"
    ),
    okButtonVariant: "outlined",
    okButton: AppColor["Primary/Primary"],
    onOk: async ({ close }) => {
      close();
    },
  });
};
